import React from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Clock, Calendar, ChevronRight } from "lucide-react";
import { recruitingAPI } from "../services/recruitingApi";

interface StaleItemsWidgetProps {
  compact?: boolean;
}

const AttentionItem: React.FC<{
  icon: React.ReactNode;
  count: number;
  label: string;
  pluralLabel: string;
  suffix: string;
  onClick: () => void;
}> = ({ icon, count, label, pluralLabel, suffix, onClick }) => (
  <div
    role="button"
    tabIndex={0}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "8px 12px",
      background: "white",
      borderRadius: "6px",
      cursor: "pointer",
    }}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      {icon}
      <span style={{ fontSize: "13px", color: "#374151" }}>
        <strong>{count}</strong> {count === 1 ? label : pluralLabel} {suffix}
      </span>
    </div>
    <ChevronRight size={14} color="#9ca3af" />
  </div>
);

const StaleItemsWidget: React.FC<StaleItemsWidgetProps> = ({
  compact = false,
}) => {
  const navigate = useNavigate();

  const { data: staleItems, isLoading } = useQuery(
    "staleItems",
    () => recruitingAPI.getStaleItems(),
    { staleTime: 5 * 60 * 1000, refetchInterval: 5 * 60 * 1000 },
  );

  if (isLoading) {
    return (
      <div style={{ padding: "16px", color: "#6b7280", fontSize: "13px" }}>
        Loading attention items...
      </div>
    );
  }

  const counts = staleItems?.counts;
  const totalCount =
    (counts?.follow_up_due || 0) +
    (counts?.stuck_candidates || 0) +
    (counts?.stale_applications || 0);

  if (totalCount === 0) {
    return null;
  }

  return (
    <div
      style={{
        background: "#fffbeb",
        border: "1px solid #fbbf24",
        borderRadius: "8px",
        padding: "16px",
        marginBottom: "16px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: compact ? "8px" : "12px",
        }}
      >
        <AlertTriangle size={18} color="#d97706" />
        <h3
          style={{
            margin: 0,
            fontSize: "14px",
            fontWeight: 600,
            color: "#92400e",
          }}
        >
          Items Needing Attention ({totalCount})
        </h3>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {(counts?.follow_up_due || 0) > 0 && (
          <AttentionItem
            icon={<Calendar size={14} color="#d97706" />}
            count={counts?.follow_up_due || 0}
            label="follow-up"
            pluralLabel="follow-ups"
            suffix="due"
            onClick={() => navigate("/candidates?status=to_come_back")}
          />
        )}

        {(counts?.stuck_candidates || 0) > 0 && (
          <AttentionItem
            icon={<Clock size={14} color="#d97706" />}
            count={counts?.stuck_candidates || 0}
            label="candidate"
            pluralLabel="candidates"
            suffix="stuck 14+ days"
            onClick={() => navigate("/candidates")}
          />
        )}

        {(counts?.stale_applications || 0) > 0 && (
          <AttentionItem
            icon={<Clock size={14} color="#d97706" />}
            count={counts?.stale_applications || 0}
            label="application"
            pluralLabel="applications"
            suffix="idle 7+ days"
            onClick={() => navigate("/pipeline")}
          />
        )}
      </div>

      {!compact &&
        staleItems?.follow_up_due &&
        staleItems.follow_up_due.length > 0 && (
          <div
            style={{
              marginTop: "12px",
              paddingTop: "12px",
              borderTop: "1px solid #fde68a",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                color: "#92400e",
                fontWeight: 500,
                marginBottom: "6px",
              }}
            >
              Upcoming follow-ups:
            </div>
            {staleItems.follow_up_due.slice(0, 3).map((item) => (
              <div
                key={item.id}
                role="button"
                tabIndex={0}
                style={{
                  fontSize: "12px",
                  color: "#374151",
                  padding: "4px 0",
                  cursor: "pointer",
                }}
                onClick={() => navigate(`/candidates/${item.id}`)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(`/candidates/${item.id}`);
                  }
                }}
              >
                {item.first_name} {item.last_name}
                {item.main_skill ? ` (${item.main_skill})` : ""} --{" "}
                {new Date(item.follow_up_date).toLocaleDateString()}
              </div>
            ))}
          </div>
        )}
    </div>
  );
};

export default StaleItemsWidget;
